import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Clock, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { InfoCircle, Xmark } from 'iconoir-react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { cn, copyTextToClipboard } from 'lib/utils';
import { useToast } from 'hooks/use-toast';
import {
  EMPTY_TEAMS_CONFIG,
  isTeamsConfigComplete,
  type TeamsConnectionConfig,
} from './teams-config';

const AZURE_PORTAL_URL = 'https://portal.azure.com/';

export type TeamsConfigureResult = {
  webhookUrl: string;
  connectionId: string;
};

type TeamsConfigModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (config: TeamsConnectionConfig) => Promise<TeamsConfigureResult | null | undefined>;
  onActivate?: (connectionId: string) => Promise<void>;
  isSaving?: boolean;
  isActivating?: boolean;
};

const inputClassName =
  'flex !h-11 w-full rounded-lg border border-grey-100 bg-white px-4 font-sans text-sm text-grey-700 shadow-none placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-transparent dark:text-true-white dark:placeholder:text-grey-400';

const pillButtonClassName =
  'h-10 gap-1.5 rounded-[62px] border border-[#D9DBE9] px-4 text-sm font-medium text-[#170F49] shadow-none hover:opacity-90';

const pillButtonStyle = {
  backgroundImage: 'linear-gradient(151deg, #FFF -62.65%, #FBFBFE 83.01%)',
} as const;

const SPATIAL_CALENDAR_CLASS_NAMES = {
  head_cell:
    'before:!content-none after:!content-none text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
  cell:
    'before:!content-none after:!content-none h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
  day:
    'h-9 w-9 p-0 font-normal text-foreground aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors',
};

function to12HourLabel(hhmm: string): string {
  const parsed = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!parsed) return hhmm;
  const hour = parseInt(parsed[1], 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${parsed[2]} ${period}`;
}

function parseDateYmd(ymd: string): Date | undefined {
  if (!ymd) return undefined;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function formatDateYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatRunAtDisplay(runAt: string): string {
  if (!runAt) return '';
  const clean = runAt.replace(/Z$/, '');
  const [datePart, timePart = '00:00'] = clean.split('T');
  const parsed = parseDateYmd(datePart);
  if (!parsed) return runAt;
  const dateFormatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
  return `${dateFormatted}, ${to12HourLabel(timePart.slice(0, 5))}`;
}

function useMuiPickerTheme() {
  const isDark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  return useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: 'hsl(var(--foreground))',
            contrastText: 'hsl(var(--background))',
          },
          background: {
            default: 'hsl(var(--background))',
            paper: 'hsl(var(--card))',
          },
          text: {
            primary: 'hsl(var(--foreground))',
            secondary: 'hsl(var(--muted-foreground))',
          },
          divider: 'hsl(var(--border))',
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          allVariants: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
        components: {
          MuiPickersLayout: {
            styleOverrides: {
              root: {
                backgroundColor: 'transparent',
                fontFamily: "'Poppins', sans-serif",
              },
            },
          },
          MuiPickersToolbar: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                padding: '6px 14px',
              },
            },
          },
          MuiTimePickerToolbar: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
              },
              hourMinuteLabel: {
                fontFamily: "'Poppins', sans-serif",
                '& .MuiTypography-root': {
                  fontFamily: "'Poppins', sans-serif",
                },
              },
              ampmSelection: {
                fontFamily: "'Poppins', sans-serif",
                '& .MuiTypography-root': {
                  fontFamily: "'Poppins', sans-serif",
                },
              },
            },
          },
          MuiClock: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: 'transparent',
              },
              clock: {
                backgroundColor: 'hsl(var(--muted))',
              },
              wrapper: {
                backgroundColor: 'transparent',
              },
            },
          },
          MuiClockNumber: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                fontSize: '0.85rem',
                fontWeight: 500,
                zIndex: 2,
                userSelect: 'none',
                color: 'hsl(var(--foreground))',
                '&.Mui-selected': {
                  fontWeight: 700,
                  color: 'hsl(var(--foreground))',
                  backgroundColor: 'transparent',
                },
              },
            },
          },
          MuiClockPointer: {
            styleOverrides: {
              root: {
                backgroundColor: 'hsl(var(--foreground))',
                zIndex: 1,
              },
              thumb: {
                borderColor: 'hsl(var(--foreground))',
                backgroundColor: 'hsl(var(--foreground))',
                opacity: 0.35,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                textTransform: 'none',
                fontWeight: 500,
              },
            },
          },
        },
      }),
    [isDark]
  );
}

function DateTimePickerField({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value || '');
  const initialValueRef = useRef(value || '');
  const muiTheme = useMuiPickerTheme();

  useEffect(() => {
    if (open) {
      initialValueRef.current = value || '';
      setDraftValue(value || '');
    }
  }, [open, value]);

  const cleanDraft = (draftValue || '').replace(/Z$/, '');
  const [datePart = '', timePart = '00:00'] = cleanDraft.split('T');
  const selectedDate = useMemo(() => parseDateYmd(datePart), [datePart]);
  const currentTime = timePart.slice(0, 5) || '00:00';
  const displayLabel = useMemo(() => formatRunAtDisplay(value), [value]);
  const draftDisplayLabel = useMemo(() => formatRunAtDisplay(draftValue), [draftValue]);

  const timeAsDate = useMemo(() => {
    const [hStr = '00', mStr = '00'] = currentTime.split(':');
    const d = new Date();
    d.setHours(parseInt(hStr, 10), parseInt(mStr, 10), 0, 0);
    return d;
  }, [currentTime]);

  const handleDateSelect = (date?: Date) => {
    if (!date) return;
    const newDateStr = formatDateYmd(date);
    setDraftValue(`${newDateStr}T${currentTime}`);
  };

  const handleTimeChange = (newTime: string) => {
    const activeDateStr = datePart || formatDateYmd(new Date());
    setDraftValue(`${activeDateStr}T${newTime || '00:00'}`);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
    } else {
      initialValueRef.current = value || '';
      setDraftValue(value || '');
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setDraftValue(initialValueRef.current);
    setOpen(false);
  };

  const handleOk = () => {
    const committed = draftValue || value || `${formatDateYmd(new Date())}T00:00`;
    onChange(committed);
    setOpen(false);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label
        htmlFor="teams-clientSecretExpiresAt"
        className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
      >
        {label}
      </label>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id="teams-clientSecretExpiresAt"
            aria-label={label}
            className={cn(
              inputClassName,
              'flex items-center justify-between text-left cursor-pointer transition-colors hover:border-grey-300 dark:hover:border-grey-600',
              !displayLabel && 'text-grey-300 dark:text-grey-400'
            )}
          >
            <span className="truncate">{displayLabel || placeholder}</span>
            <CalendarDays className="size-4 shrink-0 text-grey-500 dark:text-muted-foreground ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="top"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={16}
          style={{ zIndex: 9999 }}
          className="!z-[9999] w-auto overflow-hidden rounded-xl border-border p-0 shadow-2xl bg-card"
        >
          <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
            <div className="text-xs font-medium text-muted-foreground">{label}</div>
            <div className="text-xs font-semibold text-foreground">
              {draftDisplayLabel || t('SELECT_DATE_TIME', { defaultValue: 'Select date & time' })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center sm:items-stretch divide-y sm:divide-y-0 sm:divide-x divide-border">
            <div className="p-3 flex flex-col items-center">
              <div className="w-full pb-1 px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
                <CalendarDays className="size-3.5" />
                <span>{t('SELECT_DATE', { defaultValue: 'Select date' })}</span>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
                className="p-1"
                classNames={SPATIAL_CALENDAR_CLASS_NAMES}
              />
            </div>

            <div className="p-3 flex flex-col items-center sm:items-stretch justify-start">
              <div className="w-full pb-1 px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
                <Clock className="size-3.5" />
                <span>{t('SELECT_TIME', { defaultValue: 'Select time' })}</span>
              </div>
              <MuiThemeProvider theme={muiTheme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <StaticTimePicker
                    value={timeAsDate}
                    onChange={(newDate) => {
                      if (newDate && !isNaN(newDate.getTime())) {
                        const hh = String(newDate.getHours()).padStart(2, '0');
                        const mm = String(newDate.getMinutes()).padStart(2, '0');
                        handleTimeChange(`${hh}:${mm}`);
                      }
                    }}
                    onAccept={() => handleOk()}
                    onClose={() => setOpen(false)}
                    ampm
                    localeText={{ toolbarTitle: '' }}
                    slotProps={{ actionBar: { actions: [] } }}
                    sx={{
                      bgcolor: 'transparent',
                      fontFamily: "'Poppins', sans-serif",
                      '& .MuiPickersLayout-root': { bgcolor: 'transparent' },
                      '& .MuiPickersLayout-contentWrapper': { bgcolor: 'transparent' },
                      '& .MuiTypography-root': { fontFamily: "'Poppins', sans-serif" },
                      '& .MuiPickersToolbar-root .MuiTypography-overline': { display: 'none' },
                      '& .MuiClock-root': {
                        backgroundColor: 'transparent !important',
                        bgcolor: 'transparent !important',
                      },
                      '& .MuiClock-clock': {
                        backgroundColor: 'hsl(var(--muted)) !important',
                        bgcolor: 'hsl(var(--muted)) !important',
                      },
                      '& .MuiClockPointer-root': { zIndex: 1 },
                      '& .MuiClockPointer-thumb': { opacity: '0.35 !important' },
                      '& .MuiClockNumber-root': {
                        zIndex: '2 !important',
                        color: 'hsl(var(--foreground)) !important',
                        userSelect: 'none',
                      },
                      '& .MuiClockNumber-root.Mui-selected': {
                        fontWeight: '700 !important',
                        color: 'hsl(var(--foreground)) !important',
                        backgroundColor: 'transparent !important',
                      },
                    }}
                  />
                </LocalizationProvider>
              </MuiThemeProvider>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 bg-transparent">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="h-8 rounded-full border-border bg-background px-4 text-xs font-medium text-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
            >
              {t('CANCEL', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleOk}
              className="h-8 rounded-full bg-black-base px-4 text-xs font-medium text-true-white shadow-none hover:bg-grey-900 dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white"
            >
              {t('OK', { defaultValue: 'OK' })}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export const TeamsConfigModal = ({
  open,
  onOpenChange,
  onSave,
  onActivate,
  isSaving = false,
  isActivating = false,
}: TeamsConfigModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<TeamsConnectionConfig>(EMPTY_TEAMS_CONFIG);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const isBusy = isSaving || isActivating;
  const isAwaitingActivation = Boolean(connectionId);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_TEAMS_CONFIG);
      setWebhookUrl('');
      setConnectionId('');
    }
  }, [open]);

  const steps = [
    {
      id: 'azure-registration',
      content: t('TEAMS_STEP_1', {
        defaultValue:
          'Go to Azure Portal → Microsoft Entra ID → App registrations → New registration.',
      }),
    },
    {
      id: 'app-name',
      content: t('TEAMS_STEP_2', {
        defaultValue: 'Enter the app name, select Single tenant, and click Register.',
      }),
    },
    {
      id: 'copy-ids',
      content: t('TEAMS_STEP_3', {
        defaultValue: 'Copy the Application (Client) ID and Directory (Tenant) ID.',
      }),
    },
    {
      id: 'client-secret',
      content: t('TEAMS_STEP_4', {
        defaultValue:
          'Go to Certificates & secrets, create a New client secret, and securely save its Value.',
      }),
    },
  ];

  const detailedSteps: Array<{ id: string; content: React.ReactNode }> = useMemo(
    () => [
      {
        id: 'azure-bot-create',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_1_PREFIX', { defaultValue: 'Go to' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_1_TARGET', {
                defaultValue: 'Azure Portal → Create a resource → Azure Bot → Create',
              })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'bot-name-tenant',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_2_PREFIX', {
              defaultValue:
                'Enter a unique bot name, select the correct subscription/resource group, and choose',
            })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_2_TARGET', { defaultValue: 'Single Tenant' })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'creation-type',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_3_PREFIX', { defaultValue: 'Under' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_3_TYPE', { defaultValue: 'Creation type' })}
            </strong>
            {', '}
            {t('TEAMS_DETAIL_STEP_3_SELECT', { defaultValue: 'select' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_3_TARGET', {
                defaultValue: 'Use existing app registration',
              })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'enter-credentials',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_4_PREFIX', { defaultValue: 'Enter the provided' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_4_CLIENT_ID', {
                defaultValue: 'Microsoft Application/Client ID',
              })}
            </strong>{' '}
            {t('AND', { defaultValue: 'and' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_4_TENANT_ID', { defaultValue: 'Tenant ID' })}
            </strong>
            {', '}
            {t('TEAMS_DETAIL_STEP_4_SUFFIX', { defaultValue: 'then create the Azure Bot.' })}
          </>
        ),
      },
      {
        id: 'messaging-endpoint',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_5_PREFIX', { defaultValue: 'Open the created Azure Bot →' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_5_TARGET', { defaultValue: 'Configuration' })}
            </strong>{' '}
            {t('TEAMS_DETAIL_STEP_5_SUFFIX', { defaultValue: 'and set the messaging endpoint:' })}
            <div className="mt-1">
              <code className="break-all rounded bg-grey-100 px-1.5 py-0.5 font-mono text-xs text-grey-800 dark:bg-muted dark:text-grey-200">
                https://&lt;PUBLIC-BACKEND-DOMAIN&gt;/api/channels/teams/messages
              </code>
            </div>
          </>
        ),
      },
      {
        id: 'enable-teams-channel',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_6_PREFIX', { defaultValue: 'Open' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_6_TARGET', {
                defaultValue: 'Azure Bot → Channels → Microsoft Teams',
              })}
            </strong>
            {', '}
            {t('TEAMS_DETAIL_STEP_6_SUFFIX', {
              defaultValue: 'accept the terms, and enable the Teams channel.',
            })}
          </>
        ),
      },
      {
        id: 'teams-dev-portal',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_7_PREFIX', { defaultValue: 'Go to' })}{' '}
            <a
              href="https://dev.teams.microsoft.com/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
            >
              {t('TEAMS_DEVELOPER_PORTAL', { defaultValue: 'Teams Developer Portal' })}
            </a>{' '}
            →{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_7_APPS', { defaultValue: 'Apps → New app' })}
            </strong>{' '}
            {t('TEAMS_DETAIL_STEP_7_CREATE', { defaultValue: 'and create an app named' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">Klax AI</strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'app-details',
        content: t('TEAMS_DETAIL_STEP_8', {
          defaultValue:
            'Complete the basic app details, including description, website, privacy policy, terms, and developer information.',
        }),
      },
      {
        id: 'add-bot-feature',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_9_PREFIX', { defaultValue: 'Open' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_9_TARGET', {
                defaultValue: 'App features → Bot → Add an existing bot/Enter a bot ID',
              })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'paste-bot-id',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_10_PREFIX', { defaultValue: 'Paste the same provided' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_10_CLIENT_ID', {
                defaultValue: 'Microsoft Application/Client ID',
              })}
            </strong>{' '}
            {t('TEAMS_DETAIL_STEP_10_SUFFIX', { defaultValue: 'as the Bot ID.' })}
          </>
        ),
      },
      {
        id: 'personal-scope',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_11_PREFIX', { defaultValue: 'Enable only the' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_11_TARGET', { defaultValue: 'Personal' })}
            </strong>{' '}
            {t('TEAMS_DETAIL_STEP_11_SUFFIX', { defaultValue: 'scope for one-to-one text chat.' })}
          </>
        ),
      },
      {
        id: 'disabled-options',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_12_TITLE', { defaultValue: 'Keep these options disabled:' })}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>
                {t('TEAMS_DISABLED_OPT_FILES', { defaultValue: 'Upload and download files' })}
              </li>
              <li>
                {t('TEAMS_DISABLED_OPT_NOTIFICATIONS', {
                  defaultValue: 'Only send notifications',
                })}
              </li>
              <li>{t('TEAMS_DISABLED_OPT_AUDIO', { defaultValue: 'Audio calls' })}</li>
              <li>{t('TEAMS_DISABLED_OPT_VIDEO', { defaultValue: 'Video calls' })}</li>
            </ul>
          </>
        ),
      },
      {
        id: 'upload-icons',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_13_TITLE', { defaultValue: 'Upload:' })}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>
                <code className="rounded bg-grey-100 px-1 py-0.5 font-mono text-xs dark:bg-muted">
                  color.png
                </code>{' '}
                — 192×192
              </li>
              <li>
                <code className="rounded bg-grey-100 px-1 py-0.5 font-mono text-xs dark:bg-muted">
                  outline.png
                </code>{' '}
                — 32×32 {t('TEAMS_TRANSPARENT_PNG', { defaultValue: 'transparent PNG' })}
              </li>
            </ul>
          </>
        ),
      },
      {
        id: 'download-package',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_14_TITLE', {
              defaultValue:
                'Validate the application and download the Teams app ZIP package containing:',
            })}
            <div className="mt-1">
              <code className="break-all rounded bg-grey-100 px-1.5 py-0.5 font-mono text-xs text-grey-800 dark:bg-muted dark:text-grey-200">
                manifest.json, color.png, and outline.png
              </code>
            </div>
          </>
        ),
      },
      {
        id: 'publish-org',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_15_PREFIX', {
              defaultValue: 'In Teams Developer Portal, select',
            })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_15_TARGET', {
                defaultValue: 'Publish → Publish to your organization',
              })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'admin-center-approve',
        content: (
          <>
            {t('TEAMS_DETAIL_STEP_16_PREFIX', { defaultValue: 'Go to' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_16_MANAGE', {
                defaultValue: 'Teams Admin Center → Teams apps → Manage apps',
              })}
            </strong>
            {', '}
            {t('TEAMS_DETAIL_STEP_16_FIND', { defaultValue: 'find' })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">Klax AI</strong>
            {', '}
            {t('TEAMS_DETAIL_STEP_16_APPROVE', {
              defaultValue: 'approve/publish it, and set it to',
            })}{' '}
            <strong className="font-semibold text-grey-800 dark:text-grey-100">
              {t('TEAMS_DETAIL_STEP_16_ALLOWED', { defaultValue: 'Allowed' })}
            </strong>
            {'.'}
          </>
        ),
      },
      {
        id: 'app-policy',
        content: t('TEAMS_DETAIL_STEP_17', {
          defaultValue:
            'Allow the required users to install the app through the relevant Teams app policy.',
        }),
      },
      {
        id: 'test-chat',
        content: t('TEAMS_DETAIL_STEP_18', {
          defaultValue:
            'Install the app in Teams and test it through a personal one-to-one chat.',
        }),
      },
    ],
    [t]
  );

  const handleClose = () => {
    if (isBusy) return;
    onOpenChange(false);
  };

  const updateField = (key: keyof TeamsConnectionConfig, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleCopyWebhookUrl = async () => {
    if (!webhookUrl) return;

    try {
      await copyTextToClipboard(webhookUrl);
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('WEBHOOK_URL_COPIED', { defaultValue: 'Webhook URL copied to clipboard.' }),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: t('FAILED_TO_COPY', { defaultValue: 'Failed to copy. Please try again.' }),
      });
    }
  };

  const handleSave = async () => {
    if (!isTeamsConfigComplete(form) || !onSave) return;

    try {
      const result = await onSave({
        botName: form.botName.trim(),
        azureBotResourceName: form.azureBotResourceName.trim(),
        azureTenantId: form.azureTenantId.trim(),
        microsoftAppId: form.microsoftAppId.trim(),
        clientSecret: form.clientSecret.trim(),
        clientSecretExpiresAt: form.clientSecretExpiresAt.trim(),
      });

      const url =
        (result as any)?.webhookUrl ||
        (result as any)?.webhook_url ||
        (result as any)?.messaging_endpoint ||
        (result as any)?.messaging_endpoint_url ||
        (result as any)?.endpoint_url ||
        (result as any)?.endpointUrl ||
        (result as any)?.connection_url ||
        (result as any)?.connectionUrl ||
        (result as any)?.url ||
        (result as any)?.bot_link ||
        (result as any)?.data?.webhook_url ||
        (result as any)?.data?.webhookUrl ||
        (result as any)?.data?.messaging_endpoint ||
        (result as any)?.data?.endpoint_url ||
        (result as any)?.data?.url ||
        '';

      const connId =
        (result as any)?.connectionId ||
        (result as any)?.connection_id ||
        (result as any)?.id ||
        '';

      if (url) {
        setWebhookUrl(url);
      }
      if (connId) {
        setConnectionId(connId);
      }
    } catch {
      // Keep modal open so user can retry after error toast
    }
  };

  const handleActivate = async () => {
    if (!connectionId || !onActivate) return;

    try {
      await onActivate(connectionId);
    } catch {
      // Keep modal open so user can retry after error toast
    }
  };

  const handlePrimaryAction = () => {
    if (isAwaitingActivation) {
      void handleActivate();
      return;
    }

    void handleSave();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] min-h-0 w-full max-w-[565px] flex-col gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background"
      >
        <div className="relative min-h-0 flex-1 overflow-y-auto p-4 sm:px-8 sm:pt-8 sm:pb-5">
          <button
            type="button"
            aria-label={t('CLOSE') || 'Close'}
            onClick={handleClose}
            className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
          >
            <Xmark width={14} height={14} />
          </button>

          <div className="flex flex-col gap-4">
            <DialogTitle className="pr-6 font-sans text-[24px] font-semibold leading-[120%] text-grey-700 dark:text-true-white">
              {t('INTEGRATE_TEAMS', { defaultValue: 'Integrate Microsoft Teams' })}
            </DialogTitle>

            <div className="flex items-start gap-2 rounded bg-[#FFFAEB] px-3 py-2.5">
              <InfoCircle
                width={13.333}
                height={13.333}
                color="#725111"
                strokeWidth={1.25}
                className="mt-0.5 shrink-0"
              />
              <p className="font-sans text-[12px] leading-[160%] text-[#725111]">
                {t('TEAMS_CONNECT_DESCRIPTION', {
                  defaultValue:
                    'Connect your agent to Microsoft Teams via Azure Bot Service. Enter your Azure and bot credentials below, then save and configure the messaging endpoint.',
                })}
              </p>
            </div>

            <ol className="flex flex-col gap-2">
              {steps.map((step, index) => (
                <li key={step.id} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-semibold leading-none text-white dark:bg-foreground dark:text-background"
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <p className="font-sans text-sm leading-[150%] text-grey-700 dark:text-grey-300">
                    {step.content}
                  </p>
                </li>
              ))}
            </ol>

            <Button
              type="button"
              variant="outline"
              className={cn(pillButtonClassName, 'w-fit')}
              style={pillButtonStyle}
              onClick={() => window.open(AZURE_PORTAL_URL, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="size-3.5" aria-hidden />
              {t('OPEN_AZURE_PORTAL', { defaultValue: 'Open Azure Portal' })}
            </Button>

            <Accordion type="single" collapsible>
              <AccordionItem value="more-details" className="border-grey-100 dark:border-border">
                <AccordionTrigger className="py-2 text-sm font-semibold text-grey-700 hover:no-underline dark:text-true-white">
                  {t('TEAMS_MORE_DETAILS', { defaultValue: 'More details' })}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2.5 pb-2 text-sm leading-[150%] text-grey-700 dark:text-grey-300">
                    <p>
                      {t('TEAMS_MORE_DETAILS_INTRO', {
                        defaultValue:
                          'The Client ID, Tenant ID, and Client Secret are already available. Please complete the remaining setup as follows:',
                      })}
                    </p>
                    <ol className="list-decimal space-y-2 pl-7">
                      {detailedSteps.map((step) => (
                        <li key={step.id}>{step.content}</li>
                      ))}
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="teams-botName"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('TEAMS_BOT_NAME', { defaultValue: 'Bot Name' })}
                </label>
                <Input
                  id="teams-botName"
                  value={form.botName}
                  onChange={(event) => updateField('botName', event.target.value)}
                  placeholder={t('TEAMS_BOT_NAME_PLACEHOLDER', {
                    defaultValue: 'e.g. Customer Support Teams Bot',
                  })}
                  autoComplete="off"
                  className={inputClassName}
                />
              </div>

              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="teams-azureBotResourceName"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('TEAMS_AZURE_BOT_RESOURCE_NAME', {
                    defaultValue: 'Azure Bot Resource Name',
                  })}
                </label>
                <Input
                  id="teams-azureBotResourceName"
                  value={form.azureBotResourceName}
                  onChange={(event) => updateField('azureBotResourceName', event.target.value)}
                  placeholder={t('TEAMS_AZURE_BOT_RESOURCE_NAME_PLACEHOLDER', {
                    defaultValue: 'e.g. KLAX_BOT',
                  })}
                  autoComplete="off"
                  className={inputClassName}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="teams-azureTenantId"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('TEAMS_AZURE_TENANT_ID', { defaultValue: 'Azure Tenant ID' })}
                </label>
                <Input
                  id="teams-azureTenantId"
                  value={form.azureTenantId}
                  onChange={(event) => updateField('azureTenantId', event.target.value)}
                  placeholder={t('TEAMS_AZURE_TENANT_ID_PLACEHOLDER', {
                    defaultValue: 'e.g. 5c6dd6a7-f0c7-4a32-8f7c-9ca7cebf6e87',
                  })}
                  autoComplete="off"
                  className={inputClassName}
                />
              </div>

              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="teams-microsoftAppId"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('TEAMS_MICROSOFT_APP_ID', { defaultValue: 'Microsoft App ID' })}
                </label>
                <Input
                  id="teams-microsoftAppId"
                  value={form.microsoftAppId}
                  onChange={(event) => updateField('microsoftAppId', event.target.value)}
                  placeholder={t('TEAMS_MICROSOFT_APP_ID_PLACEHOLDER', {
                    defaultValue: 'e.g. 1c14e480-f042-4131-951d-7b0f078cdf16',
                  })}
                  autoComplete="off"
                  className={inputClassName}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="teams-clientSecret"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('TEAMS_CLIENT_SECRET', { defaultValue: 'Client Secret' })}
                </label>
                <Input
                  id="teams-clientSecret"
                  type="password"
                  value={form.clientSecret}
                  onChange={(event) => updateField('clientSecret', event.target.value)}
                  placeholder={t('TEAMS_CLIENT_SECRET_PLACEHOLDER', {
                    defaultValue: 'Value from Certificates & secrets',
                  })}
                  autoComplete="off"
                  spellCheck={false}
                  className={inputClassName}
                />
              </div>

              <DateTimePickerField
                value={form.clientSecretExpiresAt}
                onChange={(nextVal) => updateField('clientSecretExpiresAt', nextVal)}
                label={t('TEAMS_CLIENT_SECRET_EXPIRES_AT', {
                  defaultValue: 'Client Secret Expires At',
                })}
                placeholder={t('SELECT_EXPIRATION_DATE_TIME', {
                  defaultValue: 'Select expiration date & time',
                })}
              />
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <label
                htmlFor="teams-webhook-url"
                className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
              >
                {t('TEAMS_WEBHOOK_URL', { defaultValue: 'Webhook URL (Messaging endpoint)' })}
              </label>
              <div className="flex gap-2">
                <Input
                  id="teams-webhook-url"
                  readOnly
                  value={webhookUrl}
                  placeholder={t('TEAMS_WEBHOOK_URL_PLACEHOLDER', {
                    defaultValue: 'Generated after saving configuration',
                  })}
                  className={cn(
                    inputClassName,
                    !webhookUrl && 'text-grey-400 dark:text-grey-500 bg-grey-50/50 dark:bg-muted/20'
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!webhookUrl}
                  className="h-11 shrink-0 gap-1.5 rounded-lg border-grey-100 px-3 disabled:opacity-50"
                  onClick={handleCopyWebhookUrl}
                >
                  <Copy className="size-4" aria-hidden />
                  {t('COPY', { defaultValue: 'Copy' })}
                </Button>
              </div>
              <p className="font-sans text-xs leading-[160%] text-grey-500 dark:text-grey-400">
                {t('TEAMS_WEBHOOK_URL_HINT', {
                  defaultValue:
                    'Paste this URL into your Azure Bot → Configuration → Messaging endpoint, and enable the Microsoft Teams channel.',
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-grey-100 bg-white px-4 py-3 dark:border-border dark:bg-background sm:px-8">
          <Button
            type="button"
            variant="outline"
            className={cn(pillButtonClassName, 'w-[92px] px-0')}
            style={pillButtonStyle}
            disabled={isBusy}
            onClick={handleClose}
          >
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button
            type="button"
            className={cn(
              'h-10 min-w-[150px] gap-1.5 rounded-[62px] border-0 px-5 text-sm font-semibold text-white shadow-none',
              '!bg-black-base hover:!bg-grey-900',
              'disabled:!bg-grey-200 disabled:!text-grey-500 disabled:!opacity-100',
              'dark:!bg-[#3D3D3D] dark:hover:!bg-[#4F4F4F] dark:text-true-white',
              'disabled:dark:!bg-[#27272C] disabled:dark:!text-grey-500 disabled:dark:!opacity-100'
            )}
            disabled={
              isBusy ||
              (isAwaitingActivation ? !connectionId : !isTeamsConfigComplete(form))
            }
            onClick={handlePrimaryAction}
          >
            {isBusy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {isAwaitingActivation
              ? t('SAVE_AND_CONTINUE', { defaultValue: 'Save and Continue' })
              : t('SAVE_AND_VALIDATE', { defaultValue: 'Save and Validate' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
